import type { ReactNode } from 'react';
import { Link, type NavigateFn } from '@tanstack/react-router';
import { Check, Copy, Edit3, Info, Minus, TrendingDown, TrendingUp, X } from 'lucide-react';
import {
  DashboardDataTableColumnKind,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import { Button, Tooltip, TooltipContent, TooltipTrigger, cn } from '@shigo-ui/ui';

import { formatSrpm } from '../../../../dashboard/format';
import { formatCurrency, formatNumber, formatRatio } from '../../../../../lib/format';
import { dashboardTrendTone } from '../../../../dashboard/trend';
import { dashboardT } from '../../../../../lib/messages';
import { integrationStatusLabel, integrationStatusTone, type Integration } from '../../model';
import { IntegrationColumnId } from './integrationTableColumnConfig';

export interface IntegrationsSelectionColumnProps {
  selected: ReadonlySet<string>;
  onToggleRow: (id: string, checked: boolean) => void;
  headerChecked: boolean | 'indeterminate';
  onHeaderChange: (checked: boolean) => void;
}

export function createIntegrationsTableColumns(options: {
  canEdit: boolean;
  canDuplicate: boolean;
  navigate: NavigateFn;
  onShowPlacements: (integration: Integration) => void;
  showDemandPartner?: boolean;
  selection?: IntegrationsSelectionColumnProps | undefined;
}) {
  const selection = options.selection;

  const columns = buildDashboardDataTableColumns<Integration>([
    {
      id: 'selection',
      kind: DashboardDataTableColumnKind.Selection,
      hidden: !selection,
      checked: (integration) => selection?.selected.has(integration.id) ?? false,
      label: (integration) => dashboardT('integrations.selectRow', { name: integration.name }),
      onChange: (integration, checked) => selection?.onToggleRow(integration.id, checked),
      headerChecked: selection?.headerChecked,
      headerLabel: dashboardT('integrations.selectAllOnPage'),
      onHeaderChange: selection?.onHeaderChange,
      headerClassName: 'w-10 max-w-10 pl-3 pr-1',
      className: 'w-10 max-w-10 pl-3 pr-1',
    },
    {
      id: IntegrationColumnId.Name,
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('integrations.name'),
      headerClassName: 'w-72 min-w-72 pl-4',
      className: 'w-72 min-w-72 max-w-72 pl-4 font-medium',
      sortable: true,
      sortKey: 'name',
      value: (integration) => (
        <Link
          className={dashboardTableTextActionClassName()}
          title={integration.name}
          to="/demand/integrations/$integrationId/demand/$demandPartnerId/details/overview"
          params={{
            integrationId: integration.id,
            demandPartnerId: integration.demandPartner.id,
          }}
        >
          {integration.name}
        </Link>
      ),
    },
    {
      id: IntegrationColumnId.Status,
      kind: DashboardDataTableColumnKind.Status,
      header: dashboardT('integrations.status'),
      headerClassName: 'w-32',
      className: 'w-32',
      sortable: true,
      sortKey: 'status',
      label: (integration) => integrationStatusLabel(integration.status),
      tone: (integration) => integrationStatusTone(integration.status),
    },
    {
      id: 'demandPartner',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('integrations.demandPartner'),
      headerClassName: 'w-60',
      className: 'w-60 truncate',
      sortable: true,
      sortKey: 'demandPartner.displayName',
      value: (integration) =>
        integration.demandPartner.displayName ?? integration.demandPartner.name,
    },
    {
      id: IntegrationColumnId.Placements,
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('integrations.placements'),
      headerClassName: 'w-28',
      className: 'w-28',
      value: (integration) => {
        const count = integration.placementCnt ?? 0;
        return (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="max-lg:h-8 max-lg:min-w-8"
            disabled={!count}
            onClick={() => {
              options.onShowPlacements(integration);
            }}
          >
            {count}
          </Button>
        );
      },
    },
    {
      id: IntegrationColumnId.IntegrationType,
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('integrations.integrationType'),
      headerClassName: 'w-40',
      className: 'w-40',
      sortable: true,
      sortKey: 'integrationType',
      value: (integration) => integration.integrationType,
    },
    {
      id: IntegrationColumnId.Srpm,
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('integrations.srpm'),
      headerClassName: 'w-32 text-right',
      className: 'w-32 text-right tabular-nums',
      cell: (integration) => (
        <div className="flex flex-col items-end gap-0.5">
          <span>{formatSrpm(integration.srpm7d)}</span>
          {srpmChangeIndicator(integration.srpm7dChangePct ?? undefined)}
        </div>
      ),
    },
    {
      id: IntegrationColumnId.BidRatio,
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('integrations.bidRatio'),
      headerClassName: 'w-28 text-right',
      className: 'w-28 text-right tabular-nums',
      cell: (integration) => formatRatio(integration.bidRatio7d),
    },
    {
      id: IntegrationColumnId.Revenue7d,
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('integrations.revenue7d'),
      headerClassName: 'w-32 text-right',
      className: 'w-32 text-right tabular-nums',
      cell: (integration) => formatCurrency(integration.revenue7d),
    },
    {
      id: IntegrationColumnId.Ecpm7d,
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('integrations.ecpm7d'),
      headerClassName: 'w-32 text-right',
      className: 'w-32 text-right tabular-nums',
      cell: (integration) => formatCurrency(integration.ecpm7d),
    },
    {
      id: IntegrationColumnId.Qps,
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('integrations.qpsCap'),
      headerClassName: 'w-24 text-right',
      className: 'w-24 text-right tabular-nums',
      value: (integration) => integration.qps ?? '-',
    },
    {
      id: IntegrationColumnId.SellerDomain,
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('integrations.sellerDomain'),
      headerClassName: 'w-56',
      className: 'w-56 truncate',
      value: (integration) => integration.schainAsi || '-',
    },
    {
      id: IntegrationColumnId.Duplication,
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('integrations.duplication'),
      headerClassName: 'w-28 text-center',
      className: 'w-28 text-center',
      cell: (integration) =>
        integration.duplicateDemandIntegration ? (
          <Check
            role="img"
            className="text-success mx-auto h-4 w-4"
            aria-label={dashboardT('common.yes')}
          />
        ) : (
          <X
            role="img"
            className="text-muted-foreground mx-auto h-4 w-4"
            aria-label={dashboardT('common.no')}
          />
        ),
    },
    {
      id: IntegrationColumnId.Note,
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('integrations.note'),
      headerClassName: 'w-16 text-center',
      className: 'w-16 text-center',
      cell: (integration) =>
        integration.notes ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={dashboardT('integrations.viewNotes')}
              >
                <Info className="h-4 w-4" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-80 whitespace-normal">
              {integration.notes}
            </TooltipContent>
          </Tooltip>
        ) : null,
    },
    {
      id: IntegrationColumnId.Actions,
      kind: DashboardDataTableColumnKind.Actions,
      hidden: !options.canEdit && !options.canDuplicate,
      cell: (integration) => (
        <DashboardTableRowActions label={dashboardT('integrations.actions')}>
          {options.canEdit ? (
            <DashboardTableRowActionItem
              onSelect={() =>
                void options.navigate({
                  to: '/demand/integrations/edit/$demandPartnerId/$integrationId',
                  params: {
                    demandPartnerId: integration.demandPartner.id,
                    integrationId: integration.id,
                  },
                })
              }
            >
              <Edit3 className="h-4 w-4" aria-hidden="true" />
              {dashboardT('integrations.edit')}
            </DashboardTableRowActionItem>
          ) : null}
          {options.canDuplicate ? (
            <DashboardTableRowActionItem
              onSelect={() =>
                void options.navigate({
                  to: '/demand/integrations/create',
                  search: {
                    copyFrom: integration.id,
                    demandPartnerId: integration.demandPartner.id,
                  },
                })
              }
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
              {dashboardT('integrations.duplicate')}
            </DashboardTableRowActionItem>
          ) : null}
        </DashboardTableRowActions>
      ),
    },
  ]);

  return options.showDemandPartner === false
    ? columns.filter(({ id }) => id !== 'demandPartner')
    : columns;
}

function srpmChangeIndicator(value: number | undefined): ReactNode {
  if (value === undefined) return null;
  const tone = dashboardTrendTone(value);
  const Icon = srpmTrendIcon(value);
  return (
    <span className={cn('inline-flex items-center gap-1 text-[11px]', tone.className)}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {value > 0 ? '+' : ''}
      {formatNumber(value, 2)}%
    </span>
  );
}

function srpmTrendIcon(value: number) {
  if (value > 0) return TrendingUp;
  if (value < 0) return TrendingDown;
  return Minus;
}
